import { useScrollLock } from '@/shared/hooks/useScrollLock';
import { useEffect, useRef, type ReactNode } from 'react';

interface BlockingModalProps {
  isOpen: boolean;
  titleId: string;
  descriptionId?: string;
  overlayClassName?: string;
  dialogClassName?: string;
  children: ReactNode;
}

export default function BlockingModal({
  isOpen,
  titleId,
  descriptionId,
  overlayClassName = 'opacity-80',
  dialogClassName = '',
  children,
}: BlockingModalProps) {
  const modalRef = useRef<HTMLDivElement>(null);

  useScrollLock(isOpen);

  useEffect(() => {
    if (!isOpen) return;

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        event.preventDefault();
        event.stopPropagation();
      }
    };

    document.addEventListener('keydown', handleKeyDown, true);

    return () => {
      document.removeEventListener('keydown', handleKeyDown, true);
    };
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen) return;

    modalRef.current?.focus();
  }, [isOpen]);

  if (!isOpen) {
    return null;
  }

  return (
    <>
      <div
        data-blocking-guard-overlay
        className={`fixed inset-0 z-[10000] bg-bg-overlay ${overlayClassName}`}
        aria-hidden="true"
        onMouseDown={(event) => event.preventDefault()}
      />

      <div
        className="fixed inset-0 z-[10001] flex items-center justify-center p-4xl lg:p-7xl"
        role="presentation"
        onMouseDown={(event) => event.preventDefault()}
      >
        <div
          ref={modalRef}
          data-blocking-guard-dialog
          tabIndex={-1}
          role="dialog"
          aria-modal="true"
          aria-labelledby={titleId}
          aria-describedby={descriptionId}
          className={`relative flex w-full flex-col rounded-xl bg-white p-5xl shadow-lg outline-none focus:outline-none lg:p-7xl ${dialogClassName}`}
          onMouseDown={(event) => event.stopPropagation()}
        >
          {children}
        </div>
      </div>
    </>
  );
}
